import * as Yup from 'yup';
import { useEffect, useState, useRef } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  Checkbox,
  FormControlLabel,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/LockOutlined';
// component
import PropTypes from 'prop-types';
import Iconify from '../../../components/Iconify';
import axios from '../../../axios'
import { handleAccess } from 'src/utils/responseHandler';
import { getURL } from 'src/utils/config';
import { getEscStatus } from "src/api/liveCameras";
import { initializeFirebase, requestPermissionAndToken } from 'src/webPushMessage'
import { onMessage } from 'firebase/messaging'
import { handleResponse } from 'src/utils/responseHandler';
import { useTheme, alpha } from '@mui/material/styles';
import { persistLastLoggedInSession, setRememberedRestartPreference } from 'src/utils/storageProxy';
// ----------------------------------------------------------------------

const REMEMBER_USERNAME_KEY = 'rememberedUsername';
const REMEMBER_PASSWORD_KEY = 'rememberedPassword';

const getTrimmedStorageValue = (storage, key) => storage.getItem(key)?.trim() || '';

const getRememberedCredentials = () => {
  const sessionUsername = getTrimmedStorageValue(sessionStorage, REMEMBER_USERNAME_KEY);
  const sessionPassword = getTrimmedStorageValue(sessionStorage, REMEMBER_PASSWORD_KEY);

  if (sessionUsername || sessionPassword) {
    return {
      username: sessionUsername,
      password: sessionPassword,
      rememberMe: Boolean(sessionUsername && sessionPassword),
    };
  }

  // Backward compatibility: migrate old shared localStorage remember-me values
  // to tab-scoped sessionStorage so each tab can keep different credentials.
  const legacyUsername = getTrimmedStorageValue(localStorage, REMEMBER_USERNAME_KEY);
  const legacyPassword = getTrimmedStorageValue(localStorage, REMEMBER_PASSWORD_KEY);

  if (legacyUsername || legacyPassword) {
    if (legacyUsername) {
      sessionStorage.setItem(REMEMBER_USERNAME_KEY, legacyUsername);
    }
    if (legacyPassword) {
      sessionStorage.setItem(REMEMBER_PASSWORD_KEY, legacyPassword);
    }

    localStorage.removeItem(REMEMBER_USERNAME_KEY);
    localStorage.removeItem(REMEMBER_PASSWORD_KEY);
  }

  return {
    username: legacyUsername,
    password: legacyPassword,
    rememberMe: Boolean(legacyUsername && legacyPassword),
  };
};



export default function LoginForm({setToken, setAccess, setUserType, setuserData, authentication, setSubTitle, setSubTitleColor, setAuthId}) {
  const theme = useTheme();

  const [rememberedCredentials] = useState(() => getRememberedCredentials());
  const savedUsername = rememberedCredentials.username;
  const savedPassword = rememberedCredentials.password;

  const isDark = theme.palette.mode === 'dark';
  const [timeLeft, setTimeLeft] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifcationCode, setShowVerifcationCode] = useState(false);
  const [isError, setErrorMsg ] = useState({status: false, msg:""})
  const [rememberMe, setRememberMe] = useState(rememberedCredentials.rememberMe);
  const [enableUserSuggest, setEnableUserSuggest] = useState(false);
  const [enablePassSuggest, setEnablePassSuggest] = useState(false);
  const [enableVerificationCodeSuggest, setVerificationCodeSuggest] = useState(false);
  const [loginFormVariant, setLoginFormVariant] = useState(localStorage.getItem('authLogin') || 'general');

  const [authCode, setAuthCode] = useState('');
  const [authCodeError, setAuthCodeError] = useState('');
  const [showResetBtn, setShowResetBtn] = useState(false);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required')
  });
 

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getMaskedString = (str) => {
    return str.split(",")
      .map(email => {
        const [user, domain] = email.trim().split("@");
        return `${user.substring(0, 3)}...@${domain}`;
      })
      .join(", ");
  };

  useEffect(() => {
    if(loginFormVariant === "general") {
      setSubTitle('Enter Login details below');
      setSubTitleColor('text.secondary');
    } else {
      const userEmail =  localStorage.getItem('userEmail') || '';
      const loginSubTitle = `A verification code has been sent to ${userEmail ? getMaskedString(userEmail) : 'your email address'}. Please enter the code to login.`;
      setSubTitle(loginSubTitle);
      setSubTitleColor('text.secondary')
    }
  }, [loginFormVariant, setSubTitle, setSubTitleColor]);

 
  const jsonToFormData = (data) => {
    let formdata = new FormData()
    for (let x in data) 
      formdata.append(x, data[x])
    return formdata
  }

  const clearLoginError = () => {
    if (isError.status) {
      setErrorMsg({ status: false, msg: '' });
    }
  };

  const formik = useFormik({
    initialValues: {
      username: savedUsername,
      password: savedPassword,
      grant_type: 'password',
      //platform: 'web',
      // authentication:authentication
    },
    enableReinitialize: true,
    validationSchema: LoginSchema,
    onSubmit: () => {
      clearLoginError()
      handleLogin(jsonToFormData(values))      
    }
  });

  useEffect(() => {
    axios.get(getURL('current-server-time'))
      .then(res => {
        if (res.status === 200 && res.data?.current_server_time) {
          const current_server_time = res.data.current_server_time;
          startOtpCountdown(current_server_time);
        }
      })
      .catch(err => {
        console.error('Failed to fetch server time', err);
      });
  }, []);
  

  const getServerTime = () => {
    return axios.get(getURL('current-server-time'))
      .then(res => {
        if (res.status === 200 && res.data?.current_server_time) {
          return res.data.current_server_time;
        }
        return null;
      })
      .catch(err => {
        console.error('Failed to fetch server time', err);
        return null;
      });
  };

  const formatDateTime = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())} ` +
           `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

    
  const generateToken = (messaging) => {
    if (!localStorage.getItem('FCM_TOKEN')) {
      // console.log("FFFF", localStorage.getItem('FCM_TOKEN'))
      (async () => {
          const token = await requestPermissionAndToken(messaging);
          if (token) {
            localStorage.setItem("FCM_TOKEN", token);
            axios({
                method: 'post',
                url: getURL('notification/add'),
                data: {
                  DeviceId: token
                },
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('access_token')}`
                },
                validateStatus: (status) => handleResponse(status)
            }).then((response) => {
              // console.log("Device Token response: ", response)
            })
          }
      })();
    }
  }

  //const [otpExpiryTime, setOtpExpiryTime] =  useState('');
 
  
  const handleLogin = (body, type='form') => {
    //localStorage.clear()
    setToken(null)
    setUserType(null)
    setAccess(false)
    setuserData({
      "access_token": null,
      "token_type": null,      
      "ID": null,
      "IsLicenseBase": null,
      "IsAgentBased": null,
      "name": null,
      "displayname": null,
      "profile_name":null,
      "logo_url": null,
      "user_type": null,
      "client_id": null,
      "EnvironmentType":null
  })
    axios({
      method: 'post',
      url: getURL('token'),
      data: body,
      validateStatus: function (status) {
        return status >= 200 || status === 400;
      }
    }).then(function (response) {
      console.log("========================================", JSON.stringify(response.data));

      //console.log(response)

      if(response.data[0]?.VerificationStatus === 0){
        setSubmitting(false)
        setErrorMsg({status: true, msg: (response.data.error_description ?? 'Please verify your email')})
        return
      }

      initializeFirebase().then(({ _, messaging }) => {
        if (messaging) {
            // console.log("Messaging is supported and initialized.");
            generateToken(messaging)
            // Set up the onMessage handler
          //   onMessage(messaging, (payload) => {
  
          //     // Display the notification only if permission is granted
          //     if (Notification.permission === "granted") {
          //         const notification = new Notification(payload.notification.title, {
          //             body: payload.notification.body,
          //             icon: payload.notification.icon || "/default-icon.png", // Add a default icon
          //         });                  
  
          //         notification.onclick = (event) => {
          //             event.preventDefault(); // Prevent default behavior
          //             window.open(notification.data?.videoUrl || "http://localhost:3000", "_blank"); // Open in a new tab
          //         };
          //     }
          // //code here
          //   });
        } else {
            console.warn("Messaging not available in this browser.");
        }
      });

      if (type === 'form')
        setSubmitting(false)
      if (
        response.status === 200 && 
        ('access_token' in response.data) && (response.data.user_type === 9 ||response.data.user_type === 2 || response.data.user_type === 37 ||  response.data.user_type === 8 
          || response.data.user_type === 40 || response.data.user_type === 33 || response.data.user_type === 30 || response.data.user_type === 31 || response.data.user_type === 32 || response.data.user_type === 35 || response.data.user_type === 7 || response.data.user_type === 38 || response.data.user_type === 6 || response.data.user_type === 41 || response.data.user_type === 42)
        ) {
        const res = { ...response.data };
        // Ensure profile_name is never null or '', assign displayname if so
        if (!res.profile_name || res.profile_name.trim() === '') {
          res.profile_name = res.displayname;
        }

        res.EnvironmentType = typeof res.EnvironmentType !== 'undefined'
          ? (Number(res.EnvironmentType) === 0 ? 'demo' : 'general')
          : 'general';

        //res.EnvironmentType = ([1266, 1302, 1316, 1320, 1357, 1262, 1273].includes(Number( res.client_id))) ? "demo" : "general";

        for (let x in res){
          if(x==='merge_timezone_ids' || x === 'assigned_locations'){
            const timezoneDataString = JSON.stringify(res[x]);
            localStorage.setItem(x, timezoneDataString)
          }
          else{
            localStorage.setItem(x, res[x])
          }
        }

        if (rememberMe) {
          sessionStorage.setItem(REMEMBER_USERNAME_KEY, values.username?.trim() || '');
          sessionStorage.setItem(REMEMBER_PASSWORD_KEY, values.password || '');
        } else {
          sessionStorage.removeItem(REMEMBER_USERNAME_KEY);
          sessionStorage.removeItem(REMEMBER_PASSWORD_KEY);
          localStorage.removeItem(REMEMBER_USERNAME_KEY);
          localStorage.removeItem(REMEMBER_PASSWORD_KEY);
        }
        setRememberedRestartPreference(rememberMe);
          
        const AUTH_TOKEN = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`
        handleAccess (setAccess, AUTH_TOKEN)
        axios.defaults.headers.common.Authorization = AUTH_TOKEN;

        const user_client_id = res['client_id'];
        const loginUserType = res['user_type'];
        // setToken(loginUserType)
        // setUserType(loginUserType)

        const displayname = res['displayname'];
        const profile_name = res['profile_name'] && res['profile_name'].trim() !== '' ? res['profile_name'] : displayname;

        const UserAuthentication = parseInt(res['IsNeed2FAAuthentication']) || 0;
        const UserEmail = res['Email'] || '';

        setuserData({
          "access_token": res['access_token'],
          "token_type": res['token_type'],
          "ID": res['ID'],
          "IsLicenseBase": res['IsLicenseBase'],
          "IsAgentBased": res['IsAgentBased'],
          "name": res['name'],
          "displayname":displayname,
          "profile_name": profile_name,
          "logo_url": res['logo_url'],
          "user_type": res['user_type'],
          "client_id": user_client_id,
          "EnvironmentType":res['EnvironmentType']
        })

        if (parseInt(loginUserType) === 2) {
          getEscStatus(user_client_id, (response) => {
            let escStatus = 0;
            if (response.status === 200) {
              escStatus = response.data[0] ? response.data[0].EscalationStatus : 0;
            }
            localStorage.setItem('EscalationStatus', escStatus)
          });
        }

        initializeFirebase().then(({ _, messaging }) => {
          if (messaging) {
              // console.log("Messaging is supported and initialized.");
              generateToken(messaging)
              // Set up the onMessage handler
              // onMessage(messaging, (payload) => {
    
              //   // Display the notification only if permission is granted
              //   if (Notification.permission === "granted") {
              //       const notification = new Notification(payload.notification.title, {
              //           body: payload.notification.body,
              //           icon: payload.notification.icon || "/default-icon.png", // Add a default icon
              //       });                  
    
              //       notification.onclick = (event) => {
              //           event.preventDefault(); // Prevent default behavior
              //           window.open(notification.data?.videoUrl || "http://localhost:3000", "_blank"); // Open in a new tab
              //       };
              //   }
              //   //code here
              // });
          } else {
              console.warn("Messaging not available in this browser.");
          }
        });
  
        // navigate('/', { replace: true })

        console.log('response:', response.data)
        if(UserAuthentication === 1 && UserEmail !== '') {
          const otp_expiry_time = response.data?.otp_expires_at;
          const current_server_time = response.data?.current_server_time;
          const challenge_id = response.data?.challenge_id;
          localStorage.setItem('otp_expiry_time', otp_expiry_time);
          startOtpCountdown(current_server_time);
          setLoginFormVariant('2fa');
          localStorage.setItem('authLogin', '2fa');
          localStorage.setItem('authId', 0);
          localStorage.setItem('userEmail', UserEmail);
          localStorage.setItem('challenge_id', challenge_id);
        }  else {
          setLoginAccess(loginUserType);
        }

      } else if (type === 'form') {
        const errorDescription = response.status === 403 ? 'The username and password is disabled' : 'The username or password is incorrect'; 
        setErrorMsg({status: true, msg: (errorDescription)}) 
      }         
    })
  }

  const serverBaseTimeRef = useRef(null);
  const clientBasePerfRef = useRef(null);

  const startOtpCountdown = (serverNow) => {
    const parsedServerNow = new Date(serverNow);

    if(isNaN(parsedServerNow.getTime())) {
      return;
    }

    serverBaseTimeRef.current = parsedServerNow;
    clientBasePerfRef.current = performance.now(); // monotonic

    updateTimeLeft();
  };

  const updateTimeLeft = () => {
    if (!serverBaseTimeRef.current || clientBasePerfRef.current === null) return;

    const expiryTime = localStorage.getItem('otp_expiry_time');
    if (!expiryTime) {
      return;
    }

    const elapsedMs = performance.now() - clientBasePerfRef.current;

    const currentServerTime = new Date(
      serverBaseTimeRef.current.getTime() + elapsedMs
    );

    const expiry = new Date(expiryTime);
    if (Number.isNaN(expiry.getTime())) {
      return;
    }

    const secondsLeft = Math.max(
      0,
      Math.ceil((expiry.getTime() - currentServerTime.getTime()) / 1000)
    );

    setTimeLeft(secondsLeft);

    if (secondsLeft === 0) {
      setAuthCode('');
      setShowResetBtn(true);
      // Stop further countdown ticks so `timeLeft` stays at 0 for the expired UI state.
      serverBaseTimeRef.current = null;
      clientBasePerfRef.current = null;
    }
  };

  useEffect(() => {
    const timer = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);


  useEffect(() => {
    if(timeLeft === 0) {
      setAuthCode('');
      setShowResetBtn(true)
      localStorage.removeItem('otp_expiry_time');
      return;
    } 

    if (timeLeft <= 0 || timeLeft === null) return;
    setShowResetBtn(false);

  }, [timeLeft]);


  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setSubmitting } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleShowVerifcationCode = () => {
    setShowVerifcationCode((show) => !show);
  }


  const resendVerificationCode = () => {
    localStorage.removeItem('otp_expiry_time');
    console.log('resend-OTP');
    setAuthCodeError('');
    setAuthCode('');
    setTimeLeft(null);
    const UserId = parseInt(localStorage.getItem('ID'));
    const UserName = localStorage.getItem('displayname');
    const Email = localStorage.getItem('userEmail');

    const body = {
      "UserId": UserId,
      "UserName": UserName,
      "Email": Email
    }

    axios({
      method: 'post',
      url: getURL('token/resend-otp'),
      data: body,
      validateStatus: function (status) {
        return status
      }
    }).then(function (response) {
      if(response.status === 200) {
        const responseData = response.data;

        const otp_expiry_time = responseData?.otp_expires_at;
        const current_server_time = responseData?.current_server_time;
        const challenge_id = response.data?.challenge_id;
        localStorage.setItem('otp_expiry_time', otp_expiry_time);
        localStorage.setItem('challenge_id', challenge_id);
        startOtpCountdown(current_server_time);
      }
    });
  }

  const verifyVerificationCode = () => {
    if(authCode === '') {
      clearAuthCodeError('Verification code is required');
    }  else {
      const userID = parseInt(localStorage.getItem('ID'));
      const challenge_id = parseInt(localStorage.getItem('challenge_id'))

      const body = {
        "UserId": userID,
        "Otp": authCode,
        "ChallengeId":challenge_id
      }
      axios({
        method: 'post',
        url: getURL('token/verify-otp'),
        data: body,
        validateStatus: function (status) {
          return status
        }
      }).then(function (response) {
        if(response.status === 200) {
          const loginUserType = localStorage.getItem('user_type');
          setLoginAccess(loginUserType);
          localStorage.setItem('authLogin', 'general');
          localStorage.removeItem('otp_expiry_time');
        } else {
          if(response.data.detail.code === "OTP_EXPIRED") {
            setTimeLeft(0);
            setAuthCode('');
            clearAuthCodeError("Verification code has expired. Please resend the verification code.");
          } if(response.data.detail.code === "OTP_LIMIT_EXCEEDED") {
            setTimeLeft(0);
            setAuthCode('');
            clearAuthCodeError("You have made too many incorrect attempts. Please resend the verification code.");
          } else if(response.data.detail.code === "OTP_INVALID") {
            clearAuthCodeError("Invalid verification code.");
          }
        }
      });
    }
  }

  const clearAuthCodeError = (error = '') => {
    setAuthCodeError(error);
    setTimeout(() => {
      setAuthCodeError('');
    }, 3000);
  }


  const setLoginAccess = (loginUserType) => {
      localStorage.setItem('authId', 1);
      setToken(parseInt(loginUserType))
      setUserType(String(loginUserType))
      setAuthId(1)
      persistLastLoggedInSession(rememberMe);
  }


  const CONTROL_RADIUS = '12px';
  const CONTROL_HEIGHT = 60;
  const INPUT_FONT_SIZE = 14;
  const BUTTON_FONT_SIZE = 16;
  const LOGIN_INPUT_FONT_FAMILY = '"Manrope", "Segoe UI", Tahoma, sans-serif';
  const inputSx = {
    borderRadius: '10px',
    bgcolor: isDark ? 'rgba(122, 134, 170, 0.18)' : '#f3f4f7',
    height: CONTROL_HEIGHT,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#eef1f5',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255,255,255,0.16)' : '#e4e8ef',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? alpha(theme.palette.primary.main, 0.42) : '#d7dde8',
    },
    '& input::placeholder': {
      color: isDark ? alpha(theme.palette.text.secondary, 0.86) : '#9aa3af',
      fontSize: 18,
      fontFamily: LOGIN_INPUT_FONT_FAMILY,
      opacity: 1,
    },
    '& input': {
      color: isDark ? theme.palette.text.primary : '#394150',
      fontSize: INPUT_FONT_SIZE,
      fontFamily: LOGIN_INPUT_FONT_FAMILY,
      py: 0.7,
    },
  };

  
  return (
    <FormikProvider value={formik}>
      <Stack sx={{ width: '100%', maxWidth: 430, mx: 'auto' }}>
        {(isError.status) && (
          <Alert severity="error" sx={{ mb: 3, width: '100%', boxSizing: 'border-box' }}>
            {isError.msg}
          </Alert>
        )}
        {loginFormVariant === "general" ?
          (
            <Form autoComplete="off" noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Stack spacing={1.5} sx={{ mb: 2, width: '100%' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#4f5765', lineHeight: 1.1 }}>
                  Username
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="username"
                  name="username"
                  type="text"
                  placeholder='Enter username'
                  value={values.username}
                  onFocus={() => setEnableUserSuggest(true)}
                  onClick={() => setEnableUserSuggest(true)}
                  InputProps={{
                      readOnly: !enableUserSuggest,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon sx={{ fontSize: 18, color: isDark ? '#7B879F' : '#808998' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        ...inputSx,
                      },
                    }}
                  onChange={(e) => {
                    clearLoginError();
                    formik.handleChange(e);
                  }}
                  error={Boolean(touched.username && errors.username)}
                  helperText={touched.username && errors.username}
                  
                />

                <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#4f5765', mt: 0.25, lineHeight: 1.1 }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="current-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={values.password}
                  onFocus={() => setEnablePassSuggest(true)}
                  onClick={() => setEnablePassSuggest(true)}
                  InputProps={{
                    // readOnly: !enablePassSuggest,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ fontSize: 18, color: isDark ? '#7B879F' : '#808998' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end" sx={{ color: isDark ? '#7B879F' : '#808998' }}>
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      ...inputSx
                    },
                  }}
                  onChange={(e) => {
                    clearLoginError();
                    formik.handleChange(e);
                  }}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                />

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        sx={{
                          color: 'text.secondary',
                          '&.Mui-checked': {
                            color: 'primary.main'
                          }
                        }}
                      />
                    }
                    label="Keep signed in"
                    sx={{ 
                      '& .MuiFormControlLabel-label': {
                        color: '#4f5765',
                        fontSize:'1rem'
                      }
                    }}
                  />
                </div>

              </Stack>

		              <LoadingButton
		                size="large"
		                type="submit"
		                variant="contained"
		                loading={isSubmitting}
		                sx={{
		                  mt: 1,
		                  width: '100%',
		                  borderRadius: '12px',
		                  minHeight: 44,
		                  py: 0.8,
	                  fontSize: BUTTON_FONT_SIZE,
	                  fontWeight: 500,
	                  textTransform: 'none',
                  color: '#f7f8fb',
                  background: isDark ? '#202a35' : '#202a35',
                  border: '2px solid #d8dde4',
                }}
              >
                Access Dashboard
              </LoadingButton>
            </Form>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary', mt: 0.25, mb:1.5, lineHeight: 1.1 }}>
                VERIFICATION CODE
              </Typography>
              <TextField
                fullWidth
                autoComplete="one-time-code"
                name="one-time-code"
                type={showVerifcationCode ? 'text' : 'password'}
                placeholder="Enter 6-digit code"
                value={authCode}
                disabled={showResetBtn}
                onFocus={() => setVerificationCodeSuggest(true)}
                onClick={() => setVerificationCodeSuggest(true)}
                InputProps={{
           
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: isDark ? '#7B879F' : '#7f8ba7' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowVerifcationCode} edge="end" sx={{ color: isDark ? '#7B879F' : '#7f8ba7' }}>
                        <Iconify icon={showVerifcationCode ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    ...inputSx,
                  },
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  onPaste: (e) => e.preventDefault(),
                  onCopy: (e) => e.preventDefault(),
                  onCut: (e) => e.preventDefault(),
                  readOnly: !enableVerificationCodeSuggest,
                  autoComplete: 'one-time-code',
                  name: 'otp',                 // IMPORTANT: not "password"
                  'data-lpignore': 'true',     // LastPass
                  'data-form-type': 'other',   // 1Password
                  inputMode: 'numeric',
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setAuthCodeError('');
                  setAuthCode(value);
                }}
                onKeyDown={(e) => {
                  // Block non-numeric keys except control keys
                  if (
                    !/[0-9]/.test(e.key) &&
                    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)
                  ) {
                    e.preventDefault();
                  }

                  if (e.key === 'Enter') {
                    e.preventDefault();
                    verifyVerificationCode();
                  }
                }}
                error={Boolean(authCodeError)}
                helperText={authCodeError}
                />
              {(typeof timeLeft === 'number' && timeLeft !== '') &&
                <div style={{ margin:4, paddingLeft:14, paddingBottom:10, fontSize: 14, color: "#EF4444" }}>
                  {timeLeft > 0
                    ? `Verification code expires in ${formatTime(timeLeft)} minutes`
                    : "Verification code expired. Please resend."}
                </div>
              }

              {/* {showResetBtn &&  */}
                <span
                  onClick={resendVerificationCode}
                  style={{
                    borderRadius: CONTROL_RADIUS,
                    minHeight: CONTROL_HEIGHT,
                    paddingTop: '1rem',
                    paddingBottom:'1rem',
                    textDecoration: 'underline',
                    fontSize: BUTTON_FONT_SIZE,
                    fontWeight: 700,
                    cursor: 'pointer',
                    color:theme.palette.text.secondary
                  }}
                >
                  Resend Verification Code
                </span>
              {/* // } */}

              {!showResetBtn && 
                <LoadingButton
                  fullWidth
                  size="large"
                  type="button"
                  variant="contained"
                  onClick={verifyVerificationCode}
                  sx={{
                    borderRadius: CONTROL_RADIUS,
                    minHeight: CONTROL_HEIGHT,
                    py: 1.05,
                    mt:1,
                    fontSize: BUTTON_FONT_SIZE,
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  Verify
                </LoadingButton>
              }
            </>
            )
          }
      </Stack>
    </FormikProvider>
  );
}

LoginForm.propTypes = {
  setToken: PropTypes.func.isRequired
}
